import { Button } from "@/components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/contexts/AuthContext";
import {
	getProductIngredients,
	saveProductIngredients,
} from "@/hooks/use-ingredients";
import { getProductTags, saveProductTags } from "@/hooks/use-tags";
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { zodResolver } from "@hookform/resolvers/zod";
import { ImageIcon, Upload, X } from "lucide-react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import * as z from "zod";
import { CategorySelect } from "./CategorySelect";
import { IngredientsSelect } from "./IngredientsSelect";
import {
	ProductImageManager,
	ProductImageType,
} from "./ProductImageManagerEditing";
import { TagsSelect } from "./TagsSelect";

type Product = {
	id: string;
	name: string;
	description: string;
	price: number;
	case_quantity: number;
	msrp: number | null;
	category_id: string;
	sample_available: boolean;
	sample_quantity: number | null;
	sample_cost: number | null;
};

const formSchema = z.object({
	name: z.string().min(2, {
		message: "Product name must be at least 2 characters.",
	}),
	description: z.string().min(10, {
		message: "Description must be at least 10 characters.",
	}),
	price: z.number().min(0.01, {
		message: "Price must be greater than 0.",
	}),
	caseQuantity: z.coerce.number().int().min(1, {
		message: "Case quantity must be at least 1.",
	}),
	msrp: z.coerce
		.number()
		.positive({ message: "MSRP must be a positive number." }),
	categoryId: z.string().uuid({
		message: "Please select a category.",
	}),
	tags: z.array(z.string().uuid()).default([]),
	ingredients: z.array(z.string().uuid()).default([]),
	sampleAvailable: z.boolean().default(false),
	sampleQuantity: z.coerce.number().int().min(1).optional(),
	sampleCost: z.coerce.number().min(0).optional(),
});

export function EditProductForm() {
	const navigate = useNavigate();
	const { id } = useParams<{ id: string }>();
	const { user, isSupplier } = useAuth();
	const [isLoading, setIsLoading] = useState(true);
	const [product, setProduct] = useState<Product | null>(null);
	const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
	const [isUploading, setIsUploading] = useState(false);
	const [uploadProgress, setUploadProgress] = useState(0);
	const [existingImages, setExistingImages] = useState<ProductImageType[]>(
		[]
	);
	const [productTags, setProductTags] = useState<string[]>([]);
	const [productIngredients, setProductIngredients] = useState<string[]>([]);
	const [orderedImages, setOrderedImages] = useState<ProductImageType[]>([]);

	useEffect(() => {
		if (!isSupplier) {
			navigate("/dashboard");
		}
	}, [isSupplier, navigate]);

	useEffect(() => {
		async function fetchProduct() {
			if (!id) {
				toast({
					title: "Error",
					description: "Product ID is required.",
					variant: "destructive",
				});
				navigate("/dashboard/products");
				return;
			}

			setIsLoading(true);
			try {
				const { data, error } = await supabase
					.from("products")
					.select("*")
					.eq("id", id)
					.single();

				if (error) throw error;

				const { data: imagesData, error: imagesError } = await supabase
					.from("product_images")
					.select("*")
					.eq("product_id", id)
					.order("sequence", { ascending: true });

				if (imagesError) throw imagesError;

				const tags = await getProductTags(id);
				setProductTags(tags.map((tag) => tag.id));

				const ingredients = await getProductIngredients(id);
				setProductIngredients(
					ingredients.map((ingredient) => ingredient.id)
				);

				if (data) {
					setProduct(data);
					setExistingImages(imagesData || []);
				} else {
					toast({
						title: "Error",
						description: "Product not found.",
						variant: "destructive",
					});
					navigate("/dashboard/products");
				}
			} catch (error: any) {
				console.error("Error fetching product:", error);
				toast({
					title: "Error",
					description: error.message || "Failed to load product.",
					variant: "destructive",
				});
				navigate("/dashboard/products");
			} finally {
				setIsLoading(false);
			}
		}

		fetchProduct();
	}, [id, navigate]);

	const form = useForm<z.infer<typeof formSchema>>({
		resolver: zodResolver(formSchema),
		defaultValues: {
			name: product?.name || "",
			description: product?.description || "",
			price: product?.price || 0,
			caseQuantity: product?.case_quantity || 1,
			categoryId: product?.category_id || "",
			tags: [],
			ingredients: [],
			sampleAvailable: product?.sample_available || false,
			sampleQuantity: product?.sample_quantity || 1,
			sampleCost: product?.sample_cost || 0,
		},
		mode: "onChange",
	});

	useEffect(() => {
		if (product) {
			form.reset({
				name: product.name,
				description: product.description,
				price: product.price,
				caseQuantity: product.case_quantity || 1,
				msrp: product.msrp,
				categoryId: product.category_id || "",
				tags: productTags,
				ingredients: productIngredients,
				sampleAvailable: product.sample_available || false,
				sampleQuantity: product.sample_quantity || 1,
				sampleCost: product.sample_cost || 0,
			});
		}
	}, [product, form, productTags, productIngredients]);

	const uploadImages = async (productId: string): Promise<string[]> => {
		if (!selectedFiles.length) return [];

		setIsUploading(true);
		const imageUrls: string[] = [];

		try {
			for (let i = 0; i < selectedFiles.length; i++) {
				const file = selectedFiles[i];
				const fileExt = file.name.split(".").pop();
				const fileName = `${
					user!.id
				}/${productId}/${Date.now()}.${fileExt}`;
				const filePath = `${fileName}`;

				const { error: uploadError } = await supabase.storage
					.from("product_images")
					.upload(filePath, file);

				if (uploadError) throw uploadError;

				const {
					data: { publicUrl },
				} = supabase.storage
					.from("product_images")
					.getPublicUrl(filePath);

				imageUrls.push(publicUrl);
				setUploadProgress(((i + 1) / selectedFiles.length) * 100);
			}

			return imageUrls;
		} catch (error) {
			console.error("Error uploading images:", error);
			throw error;
		} finally {
			setIsUploading(false);
			setUploadProgress(0);
		}
	};

	const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const files = e.target.files;
		if (files) {
			const newFiles = Array.from(files);
			setSelectedFiles([...selectedFiles, ...newFiles]);
		}
	};

	const removeFile = (index: number) => {
		const newFiles = [...selectedFiles];
		newFiles.splice(index, 1);
		setSelectedFiles(newFiles);
	};

	const handleDeleteImage = async (imageId: string) => {
		try {
			const { error } = await supabase
				.from("product_images")
				.delete()
				.eq("id", imageId);

			if (error) throw error;

			const updatedImages = existingImages.filter(
				(img) => img.id !== imageId
			);

			if (updatedImages.length > 0) {
				const resequencedImages = updatedImages.map((img, index) => ({
					...img,
					sequence: index,
					is_primary: index === 0,
				}));

				for (const img of resequencedImages) {
					await supabase
						.from("product_images")
						.update({
							sequence: img.sequence,
							is_primary: img.is_primary,
						})
						.eq("id", img.id);
				}

				setExistingImages(resequencedImages);
			} else {
				setExistingImages([]);
			}

			toast({
				title: "Success",
				description: "Image deleted successfully.",
			});
		} catch (error) {
			console.error("Error deleting image:", error);
			toast({
				title: "Error",
				description: "Failed to delete image.",
				variant: "destructive",
			});
		}
	};

	const handleImageReorder = async (images: ProductImageType[]) => {
		try {
			const existingImagesOnly = images.filter((img) => img.id);

			const updates = existingImagesOnly.map((image, index) => ({
				id: image.id,
				sequence: index,
				is_primary: index === 0,
			}));

			for (const update of updates) {
				const { error } = await supabase
					.from("product_images")
					.update({
						sequence: update.sequence,
						is_primary: update.is_primary,
					})
					.eq("id", update.id);

				if (error) throw error;
			}

			setExistingImages(
				existingImagesOnly.map((img, index) => ({
					...img,
					sequence: index,
					is_primary: index === 0,
				}))
			);

			toast({
				title: "Success",
				description: "Image order updated successfully.",
			});
		} catch (error) {
			console.error("Error reordering images:", error);
			toast({
				title: "Error",
				description: "Failed to update image order.",
				variant: "destructive",
			});
		}
	};

	async function onSubmit(values: z.infer<typeof formSchema>) {
		setIsLoading(true);
		try {
			// First update the product details
			const { error } = await supabase
				.from("products")
				.update({
					name: values.name,
					description: values.description,
					price: parseFloat(String(values.price)),
					case_quantity: values.caseQuantity,
					msrp: values.msrp || null,
					category_id: values.categoryId,
					sample_available: values.sampleAvailable,
					sample_quantity: values.sampleAvailable
						? values.sampleQuantity
						: null,
					sample_cost: values.sampleAvailable
						? values.sampleCost
						: null,
				})
				.eq("id", id);

			if (error) throw error;

			await saveProductTags(id!, values.tags);
			await saveProductIngredients(id!, values.ingredients);

			// Handle image uploads with correct sequencing
			if (selectedFiles.length > 0) {
				// Upload new files first
				const uploadPromises = selectedFiles.map(async (file) => {
					const fileExt = file.name.split(".").pop();
					const fileName = `${
						user!.id
					}/${id}/${Date.now()}.${fileExt}`;
					const filePath = `${fileName}`;

					const { error: uploadError } = await supabase.storage
						.from("product_images")
						.upload(filePath, file);

					if (uploadError) throw uploadError;

					const {
						data: { publicUrl },
					} = supabase.storage
						.from("product_images")
						.getPublicUrl(filePath);

					return { file, publicUrl };
				});

				const uploadedFiles = await Promise.all(uploadPromises);

				// Create a map of file names to their uploaded URLs
				const fileUrlMap = new Map(
					uploadedFiles.map(({ file, publicUrl }) => [
						file.name,
						publicUrl,
					])
				);

				// Get the final sequence from orderedImages
				const finalSequence = orderedImages.map((img, index) => {
					if (img.isNew && img.file) {
						// This is a new image
						return {
							product_id: id,
							image_url: fileUrlMap.get(img.file.name)!,
							is_primary: index === 0,
							sequence: index,
						};
					} else {
						// This is an existing image
						return {
							...img,
							is_primary: index === 0,
							sequence: index,
						};
					}
				});

				// Separate existing and new images
				const existingImagesToUpdate = finalSequence.filter(
					(img) => !img.isNew && img.id
				);
				const newImagesToInsert = finalSequence.filter(
					(img) => !img.id
				);

				// Update existing images
				for (const img of existingImagesToUpdate) {
					await supabase
						.from("product_images")
						.update({
							sequence: img.sequence,
							is_primary: img.is_primary,
						})
						.eq("id", img.id);
				}

				// Insert new images
				if (newImagesToInsert.length > 0) {
					const { error: insertError } = await supabase
						.from("product_images")
						.insert(newImagesToInsert);

					if (insertError) throw insertError;
				}

				// Fetch the final state of images
				const { data: updatedImages } = await supabase
					.from("product_images")
					.select("*")
					.eq("product_id", id)
					.order("sequence", { ascending: true });

				setExistingImages(updatedImages || []);
				setSelectedFiles([]);
			}

			toast({
				title: "Success",
				description: "Product updated successfully.",
			});
			navigate("/dashboard/products");
		} catch (error: any) {
			console.error("Error updating product:", error);
			toast({
				title: "Error",
				description: error.message || "Failed to update product.",
				variant: "destructive",
			});
		} finally {
			setIsLoading(false);
		}
	}

	if (isLoading) {
		return (
			<div className="w-full max-w-2xl mx-auto">
				<Card>
					<CardHeader>
						<CardTitle>
							<Skeleton className="h-6 w-60" />
						</CardTitle>
						<CardDescription>
							<Skeleton className="h-4 w-40" />
						</CardDescription>
					</CardHeader>
					<CardContent className="grid gap-4">
						<div className="grid gap-2">
							<div className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
								<Skeleton className="h-4 w-32" />
							</div>
							<Skeleton className="h-10 w-full" />
						</div>
						<div className="grid gap-2">
							<div className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
								<Skeleton className="h-4 w-32" />
							</div>
							<Skeleton className="h-24 w-full" />
						</div>
						<div className="grid grid-cols-2 gap-4">
							<div className="grid gap-2">
								<div className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
									<Skeleton className="h-4 w-24" />
								</div>
								<Skeleton className="h-10 w-full" />
							</div>
							<div className="grid gap-2">
								<div className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
									<Skeleton className="h-4 w-24" />
								</div>
								<Skeleton className="h-10 w-full" />
							</div>
						</div>
						<div className="grid gap-2">
							<div className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
								<Skeleton className="h-4 w-32" />
							</div>
							<Skeleton className="h-10 w-full" />
						</div>
						<div className="space-y-4">
							<div>
								<h3 className="text-lg font-medium">
									Product Images
								</h3>
								<p className="text-sm text-gray-500">
									Upload high-quality images of your product.
									The first image will be used as the primary
									image.
								</p>
							</div>

							{existingImages.length > 0 && (
								<div className="border border-gray-200 rounded-md p-4">
									<div className="grid grid-cols-2 md:grid-cols-3 gap-4">
										{existingImages.map((image) => (
											<div
												key={image.id}
												className="relative group">
												<img
													src={image.image_url}
													alt="Product"
													className="w-full aspect-square object-cover rounded-md"
												/>
												{image.is_primary && (
													<span className="absolute top-2 left-2 bg-primary/10 text-primary px-2 py-1 rounded text-xs">
														Primary
													</span>
												)}
												<button
													type="button"
													onClick={() =>
														handleDeleteImage(
															image.id
														)
													}
													className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity">
													<X className="h-4 w-4" />
												</button>
											</div>
										))}
									</div>
								</div>
							)}

							<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
								<div className="border-2 border-dashed border-gray-300 rounded-md p-4 text-center">
									<label className="cursor-pointer">
										<div className="flex flex-col items-center gap-2">
											<Upload className="h-8 w-8 text-gray-400" />
											<span className="font-medium">
												Upload Images
											</span>
											<span className="text-xs text-gray-500">
												PNG, JPG, WEBP up to 5MB
											</span>
										</div>
										<input
											type="file"
											accept="image/*"
											multiple
											onChange={handleFileChange}
											className="hidden"
											disabled={isLoading || isUploading}
										/>
									</label>
								</div>

								<div className="border border-gray-200 rounded-md p-4">
									<h4 className="font-medium mb-2">
										Selected New Images (
										{selectedFiles.length})
									</h4>
									{selectedFiles.length === 0 ? (
										<div className="flex flex-col items-center justify-center h-20 text-gray-400">
											<ImageIcon className="h-6 w-6 mb-1" />
											<span className="text-sm">
												No images selected
											</span>
										</div>
									) : (
										<ul className="space-y-2 max-h-40 overflow-y-auto">
											{selectedFiles.map(
												(file, index) => (
													<li
														key={index}
														className="flex items-center justify-between bg-gray-50 p-2 rounded">
														<div className="flex items-center">
															<ImageIcon className="h-4 w-4 mr-2 text-gray-500" />
															<span className="text-sm truncate max-w-[150px]">
																{file.name}
															</span>
														</div>
														<button
															type="button"
															onClick={() =>
																removeFile(
																	index
																)
															}
															className="text-gray-500 hover:text-red-500"
															disabled={
																isLoading ||
																isUploading
															}>
															<X className="h-4 w-4" />
														</button>
													</li>
												)
											)}
										</ul>
									)}
								</div>
							</div>

							{isUploading && (
								<div className="w-full bg-gray-200 rounded-full h-2.5">
									<div
										className="bg-primary h-2.5 rounded-full"
										style={{
											width: `${uploadProgress}%`,
										}}></div>
								</div>
							)}
						</div>
						<Button
							variant="accent"
							type="submit"
							disabled={isLoading}>
							Update Product
						</Button>
					</CardContent>
				</Card>
			</div>
		);
	}

	return (
		<div className="w-full max-w-2xl mx-auto">
			<Card>
				<CardHeader>
					<CardTitle>Edit Product</CardTitle>
					<CardDescription>Update product details.</CardDescription>
				</CardHeader>
				<CardContent>
					<Form {...form}>
						<form
							onSubmit={form.handleSubmit(onSubmit)}
							className="space-y-6">
							<FormField
								control={form.control}
								name="name"
								render={({ field }) => (
									<FormItem>
										<FormLabel>Product Name</FormLabel>
										<FormControl>
											<Input
												placeholder="Product Name"
												{...field}
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>
							<FormField
								control={form.control}
								name="description"
								render={({ field }) => (
									<FormItem>
										<FormLabel>Description</FormLabel>
										<FormControl>
											<Textarea
												placeholder="Product Description"
												className="resize-none"
												{...field}
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>
							<FormField
								control={form.control}
								name="caseQuantity"
								render={({ field }) => (
									<FormItem>
										<FormLabel>Case Quantity</FormLabel>
										<FormControl>
											<Input
												type="number"
												min="1"
												step="1"
												placeholder="1"
												{...field}
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>
							<div className="grid grid-cols-2 gap-4">
								<FormField
									control={form.control}
									name="price"
									render={({ field }) => (
										<FormItem>
											<FormLabel>Price</FormLabel>
											<FormControl>
												<Input
													type="number"
													placeholder="0.00"
													{...field}
													onChange={(e) =>
														field.onChange(
															parseFloat(
																e.target.value
															)
														)
													}
												/>
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
								<FormField
									control={form.control}
									name="msrp"
									render={({ field }) => (
										<FormItem>
											<FormLabel>MSRP</FormLabel>
											<FormControl>
												<Input
													type="number"
													step="0.01"
													min="0"
													placeholder="0.00"
													value={field.value || ""}
													onChange={(e) =>
														field.onChange(
															parseFloat(
																e.target.value
															)
														)
													}
												/>
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
							</div>
							<FormField
								control={form.control}
								name="categoryId"
								render={({ field }) => (
									<FormItem>
										<FormLabel>Category</FormLabel>
										<FormControl>
											<CategorySelect
												value={field.value}
												onChange={field.onChange}
												placeholder="Select a category"
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>
							<FormField
								control={form.control}
								name="tags"
								render={({ field }) => (
									<FormItem>
										<FormLabel>Product Tags</FormLabel>
										<FormControl>
											<TagsSelect
												value={field.value}
												onChange={field.onChange}
												placeholder="Select product tags"
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>
							<FormField
								control={form.control}
								name="ingredients"
								render={({ field }) => (
									<FormItem>
										<FormLabel>
											Product Ingredients
										</FormLabel>
										<FormControl>
											<IngredientsSelect
												value={field.value}
												onChange={field.onChange}
												placeholder="Select or create ingredients"
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>

							{/* Sample Information Section */}
							<div className="p-4 bg-blue-50 rounded-lg">
								<h3 className="text-lg font-medium mb-2">
									Sample Information
								</h3>
								<p className="text-sm text-gray-600 mb-4">
									Offer product samples to retailers to try
									before making larger orders
								</p>

								<FormField
									control={form.control}
									name="sampleAvailable"
									render={({ field }) => (
										<FormItem className="flex flex-row items-center space-x-3 space-y-0 mb-4">
											<FormControl>
												<input
													type="checkbox"
													checked={field.value}
													onChange={field.onChange}
													className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
												/>
											</FormControl>
											<FormLabel className="cursor-pointer">
												This product has samples
												available
											</FormLabel>
										</FormItem>
									)}
								/>

								{form.watch("sampleAvailable") && (
									<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
										<FormField
											control={form.control}
											name="sampleQuantity"
											render={({ field }) => (
												<FormItem>
													<FormLabel>
														Sample Quantity
													</FormLabel>
													<FormControl>
														<Input
															type="number"
															min="1"
															step="1"
															placeholder="1"
															{...field}
														/>
													</FormControl>
													<FormMessage />
												</FormItem>
											)}
										/>

										<FormField
											control={form.control}
											name="sampleCost"
											render={({ field }) => (
												<FormItem>
													<FormLabel>
														Sample Cost ($)
													</FormLabel>
													<FormControl>
														<Input
															type="number"
															step="0.01"
															min="0"
															placeholder="0.00"
															{...field}
														/>
													</FormControl>
													<FormMessage />
												</FormItem>
											)}
										/>
									</div>
								)}
							</div>

							{/* Product Images Section */}
							<ProductImageManager
								existingImages={existingImages}
								selectedFiles={selectedFiles}
								setSelectedFiles={setSelectedFiles}
								isUploading={isUploading}
								uploadProgress={uploadProgress}
								onDeleteImage={handleDeleteImage}
								onReorder={handleImageReorder}
								onOrderChange={setOrderedImages}
							/>

							<Button
								variant="accent"
								type="submit"
								disabled={isLoading}>
								Update Product
							</Button>
						</form>
					</Form>
				</CardContent>
			</Card>
		</div>
	);
}
